<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useInventoryDashboardStore } from '../pages/useInventoryDashboardStore';
import { InventoryDashboardViewModel } from '../pages/inventoryDashboardViewModel';
import { i18n } from '@/i18n';
import { UNASSIGNED_COLUMN } from '@/coreDomainsModule/constants';
import { computed, provide, ref } from 'vue';
import { DashboardService } from '../domains/services/DashboardService';
import { isTableType } from '@/coreDomainsModule/table';
import { useTableHeader } from '@/corePagesModule/composables/useTableHeader';
import ListViewSectionTable from './ListViewSectionTable.vue';
import { useColumnWidth } from '@/corePagesModule/composables/useColumnWidth';
import {
  LIST_VIEW_AGG_BASE_HEADER_KEYS,
  LIST_VIEW_BASE_HEADER_KEYS,
  LIST_VIEW_BIZ_BASE_HEADER_KEYS,
  LIST_VIEW_CUSTOM_HEADER_KEYS,
  LIST_VIEW_SECTION_INJECTION_KEY,
} from '../constants';
const t = i18n.t;

const store = useInventoryDashboardStore();
const { state } = storeToRefs(store);
const {} = store;
const vm = new InventoryDashboardViewModel();

const tableWidthInfo = ref<Record<string, number | undefined>>({});

const isAggregateBusiness = computed(() => DashboardService.isMetricTypeByAggBiz(state.value.information.metricType));
const fullHeaderOptions = computed(() => [
  ...LIST_VIEW_BASE_HEADER_KEYS,
  ...(isAggregateBusiness.value ? LIST_VIEW_AGG_BASE_HEADER_KEYS : LIST_VIEW_BIZ_BASE_HEADER_KEYS),
  ...LIST_VIEW_CUSTOM_HEADER_KEYS[state.value.information.metricType],
  { key: 'headerModify', width: 57 },
]);
const fullHeaderKeys = computed(() => fullHeaderOptions.value.map((h) => h.key));
const tableType = computed(() => {
  const rawTableType =
    'inventoryDashboardByDimension' +
    state.value.information.metricType
      .toLowerCase()
      .split('_')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join('');

  if (!isTableType(rawTableType)) {
    console.error(`Invalid table type: ${rawTableType}`);
    throw new Error(`Invalid table type: ${rawTableType}`);
  }
  return rawTableType;
});

const { headerList, updateDisplayHeader } = useTableHeader(fullHeaderKeys, tableType, []);
useColumnWidth({
  tableType: tableType,
  widthInfo: tableWidthInfo,
});

/**
 * チェックされたかどうか
 * @param businessId - 対象の取引ID
 * @param isChecked - チェック済みかどうか
 */
const onCheckedRow = (businessId: string, isChecked: boolean) => {
  if (isChecked && !state.value.checkedBusinessId.includes(businessId)) {
    state.value.checkedBusinessId.push(businessId);
  } else if (!isChecked && state.value.checkedBusinessId.includes(businessId)) {
    state.value.checkedBusinessId = state.value.checkedBusinessId.filter((b) => b !== businessId);
  }
};

/**
 * ヘッダーを切り替える処理
 * @param headerKey - ヘッダーキー
 * @param isHide - 隠すかどうか
 */
const onToggleHeader = async (headerKey: string, isHide: boolean) => {
  await updateDisplayHeader(headerKey, isHide);
};

provide(LIST_VIEW_SECTION_INJECTION_KEY, tableWidthInfo);
</script>
<template>
  <div class="list-view">
    <div v-for="(dimensionValueGroup, index) in state.information.dimensionValueGroups" :key="index" class="item">
      <div class="item__title">
        <div class="item__title--dimension">
          {{ t(vm.dimensionTypesMapper[state.information.dimensionType] ?? '') }}
        </div>
        <div class="item__title--name">
          <template
            v-if="
              dimensionValueGroup.dimensionValues.name ||
              dimensionValueGroup.dimensionValues.value ||
              dimensionValueGroup.dimensionValues.unit
            "
          >
            {{ dimensionValueGroup.dimensionValues.name }} {{ dimensionValueGroup.dimensionValues.value }}
            {{ dimensionValueGroup.dimensionValues.unit }}
          </template>
          <template v-else>
            {{ t(UNASSIGNED_COLUMN) }}
          </template>
        </div>
      </div>
      <div class="item__metric-type">
        {{ t(vm.metricTypesMapper[state.information.metricType] ?? '') }}
      </div>
      <div class="item__table base-table-wrapper">
        <ListViewSectionTable
          :businesses="dimensionValueGroup.businesses"
          :checked-business-id="state.checkedBusinessId"
          :table-type="tableType"
          :full-header-options="fullHeaderOptions"
          :header-list="headerList"
          :is-aggregate-business="isAggregateBusiness"
          @on-toggle-header="onToggleHeader"
          @on-checked-row="onCheckedRow"
        />
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.list-view {
  display: grid;
  gap: 16px;
}
.item {
  display: grid;
  gap: 8px;
  padding: 24px 16px 16px 16px;
  border-radius: 8px;
  border: 1px solid $borderDefault;
  box-shadow: 0px 0px 22px 0px #80808066;

  &__title {
    display: grid;
    grid-template-columns: max-content max-content;
    gap: 22px;
    * {
      color: $textSecondary;
      font-size: 22px;
      font-weight: 700;
    }
  }

  &__metric-type {
    padding: 4px 0;
    color: $textSecondary;
    font-size: 16px;
    font-weight: 700;
  }

  &__table {
    overflow-x: auto;
    :deep(.table__header-container) {
      width: 100%;
    }
  }
}
</style>
